import $http from '../services/AxiosInstance';
import { ErrorApi } from '../types/api.type';
import {
  ApiResponseIUser,
  IPermission,
  IRole,
  User,
  UserApi as UserApiEntity,
} from '../types/user.type';
import storeAuth from '@store/auth';
import { loadLocaleMessages } from '../helpers/lang/loadLocale';
import i18n from '../helpers/lang/i18n';

export interface GetAuthResponse extends ErrorApi {
  user?: User;
  token?: string;
}
export interface GetUserResponse extends ErrorApi {
  data?: User;
}

export interface GetPermissionResponse extends ErrorApi {
  data?: IPermission[];
}
export interface GetRoleResponse extends ErrorApi {
  data?: IRole[];
}

interface Params {
  page?: number;
  searchData?: {
    search?: string | null;
    roleId?: number | null;
    companyId?: number | null;
    countryId?: number | null;
  } | null;
}

class UserApi implements UserApiEntity {
  async getAuth(): Promise<GetAuthResponse | null> {
    const store = storeAuth();

    // Sin cookie no tiene sentido intentar el refresh.
    // La cookie HttpOnly no es accesible desde JS, pero si el user nunca hizo login,
    // tampoco hay 'user' en localStorage.
    const getUserLocalStorage = localStorage.getItem('user');
    if (!getUserLocalStorage) return null;

    try {
      // La cookie HttpOnly del refresh token se envía automáticamente
      // gracias a withCredentials: true en la instancia de Axios.
      // No se envía ningún token en el body.
      const { data } = await $http.post('auth/refresh');
      store.refresh(data);

      const lang = data.user.language?.fileJson || 'es_ar';
      const messages = loadLocaleMessages(lang);
      i18n.global.setLocaleMessage(lang, messages);
      i18n.global.locale.value = lang;
      return data;
    } catch (error: any) {
      return { error };
    }
  }

  async getUsers(params: Params): Promise<ApiResponseIUser> {
    try {
      let url = `users?page=${params.page}`;

      if (params.searchData !== null) {
        if (!!params.searchData?.search && params.searchData?.search.trim() !== '') {
          url += `&search=${params.searchData?.search ?? ''}`;
        }
        if (!!params.searchData?.roleId) {
          url += `&roleId=${params.searchData.roleId}`;
        }
        if (!!params.searchData?.companyId) {
          url += `&companyId=${params.searchData.companyId}`;
        }
        if (!!params.searchData?.countryId) {
          url += `&countryId=${params.searchData.countryId}`;
        }
      }

      const { data } = await $http.get(url);

      return data;
    } catch (error: any) {
      throw error;
    }
  }

  async getAllUserMinimalData(): Promise<ApiResponseIUser> {
    try {
      const { data } = await $http.get(`users/minimal/all`);
      return data;
    } catch (error: any) {
      throw error;
    }
  }

  async getPermissions(): Promise<GetPermissionResponse> {
    try {
      const { data } = await $http.get(`users/permissions/all`);
      return data;
    } catch (error: any) {
      return { error };
    }
  }

  async getRoles(params: Params | null = null) {
    try {
      const url = params
        ? `users/roles/all?page=${params.page}`
        : `users/roles/all`;
      const { data } = await $http.get(url);

      if (params) {
        return data;
      }

      const rolesForSelect = data.data.map((e: IRole) => ({
        id: e.id,
        title: e.title,
        description: e.description,
      }));

      return rolesForSelect;
    } catch (error: any) {
      throw error;
    }
  }

  async getPermissionsByRoleId(id: number) {
    try {
      const { data } = await $http.get(`users/roles/${id}/permissions`);
      return data;
    } catch (error: any) {
      return { error };
    }
  }

  async createRole(body: any) {
    try {
      const { data } = await $http.post('/users/roles', body);

      return data;
    } catch (error: any) {
      throw error;
    }
  }

  async createUser(body: any): Promise<GetUserResponse> {
    try {
      const { data } = await $http.post('users', body);

      return data;
    } catch (error: any) {
      throw error;
    }
  }

  async updateUser(id: number, body: any): Promise<GetUserResponse> {
    try {
      const { data } = await $http.patch(`users/${id}`, body);

      return data;
    } catch (error: any) {
      throw error;
    }
  }

  async updateRole(id: number, body: any) {
    try {
      const { data } = await $http.patch(`users/roles/${id}`, body);

      return data;
    } catch (error: any) {
      throw error;
    }
  }

  async blockUser(id: number): Promise<GetUserResponse> {
    try {
      const { data } = await $http.delete(`users/${id}`);

      return data;
    } catch (error: any) {
      throw error;
    }
  }

  async unBlockUser(id: number): Promise<GetUserResponse> {
    try {
      const { data } = await $http.patch(`users/restore/${id}`);
      return data;
    } catch (error: any) {
      throw error;
    }
  }
}

export default UserApi;
